// Pass 1 — the Sun, one texel per character cell.
// Photosphere: animated 3D fBM granulation with domain warp + limb darkening.
// Corona: rayed streamers in polar space that slowly turn, plus an inner glow.
// `dusk` (0 = noon, 1 = set) reddens and dims the palette and flattens the
// corona along the horizon as the Sun sinks. Output: rgb = color, a = luminance.
import { fbmSimplex2d, fbmSimplex3d } from "@vgpu/wgsl-std/noise/simplex";

struct Params {
  time: f32,
  aspect: f32,
  center: vec2f,
  radius: f32,
  /** eased cursor position in uv; (0.5, 0.5) when idle */
  pointer: vec2f,
  /** 0 at the top of the page, 1 when the Sun has set */
  dusk: f32,
}

@group(0) @binding(0) var<uniform> params: Params;

fn palette(v: f32, dusk: f32) -> vec3f {
  let ember = mix(vec3f(0.32, 0.03, 0.0), vec3f(0.20, 0.02, 0.0), dusk);
  let orange = mix(vec3f(0.96, 0.30, 0.04), vec3f(0.82, 0.16, 0.03), dusk);
  let gold = mix(vec3f(1.0, 0.72, 0.16), vec3f(0.98, 0.42, 0.09), dusk);
  let core = mix(vec3f(1.0, 0.97, 0.80), vec3f(1.0, 0.74, 0.40), dusk);
  let x = clamp(v, 0.0, 1.0);
  let lo = mix(ember, orange, smoothstep(0.0, 0.36, x));
  let hi = mix(gold, core, smoothstep(0.72, 1.0, x));
  return mix(lo, hi, smoothstep(0.36, 0.72, x));
}

@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let t = params.time;
  let dusk = params.dusk;
  let p = (uv - params.center) * vec2f(params.aspect, 1.0) / params.radius;
  let r = length(p);

  var v = 0.0;

  // --- photosphere -----------------------------------------------------------
  if (r < 1.0) {
    let mu = sqrt(max(0.0, 1.0 - r * r));
    let limb = 0.64 + 0.36 * mu;
    let warp = vec2f(
      fbmSimplex3d(vec3f(p * 1.6, t * 0.05), 3, 2.1, 0.5),
      fbmSimplex3d(vec3f(p * 1.6 + vec2f(5.2, 1.3), t * 0.05), 3, 2.1, 0.5),
    ) * 0.35;
    let gran = fbmSimplex3d(vec3f(p * 3.4 + warp, t * 0.09), 5, 2.15, 0.52) * 0.5 + 0.5;
    let cells = fbmSimplex3d(vec3f(p * 9.0 - warp * 0.5, t * 0.14), 3, 2.2, 0.5) * 0.5 + 0.5;
    let surface = 0.50 + 0.38 * gran + 0.14 * cells;
    v = surface * limb * 1.12 + 0.10 * (1.0 - r) * (1.0 - r);
    // dark spots
    let spot = smoothstep(0.62, 0.78, fbmSimplex3d(vec3f(p * 2.2 + vec2f(9.0, 3.0), t * 0.03), 2, 2.0, 0.5));
    v *= 1.0 - 0.28 * spot * mu * mu;
  }

  // --- corona ------------------------------------------------------------------
  // At dusk the corona flattens: squash the vertical axis so streamers lie
  // along the horizon.
  let pc = vec2f(p.x, p.y * (1.0 + 2.4 * dusk));
  let rc = length(pc);
  let ang = atan2(pc.y, pc.x);
  let d = max(rc - 1.0, 0.0);
  // long radial streamers: high frequency around, low frequency outward
  let broad = fbmSimplex2d(vec2f(ang * 3.2 + t * 0.012, d * 0.55 - t * 0.03), 4, 2.1, 0.55) * 0.5 + 0.5;
  let fine = fbmSimplex2d(vec2f(ang * 11.0 - t * 0.02, d * 0.9 + 3.0), 3, 2.2, 0.5) * 0.5 + 0.5;
  var rays = pow(broad, 2.2) * (0.6 + 0.4 * fine);
  // reach: streamers strengthen toward the cursor
  let toCursor = (params.pointer - params.center) * vec2f(params.aspect, 1.0);
  let reach = length(toCursor) / params.radius;
  let toward = dot(normalize(pc + vec2f(1e-5)), normalize(toCursor + vec2f(1e-5)));
  rays *= 1.0 + 0.9 * pow(max(toward, 0.0), 5.0) * smoothstep(0.6, 1.6, reach);
  let corona = exp(-d * 2.3) * (0.06 + 0.94 * rays) * 0.42;
  let glow = exp(-d * 6.5) * 0.20;
  let edge = 1.0 - smoothstep(0.985, 1.0, r);
  v = max(v, (corona + glow) * (1.0 - edge));

  // a breath across the whole disc
  v *= 0.97 + 0.03 * sin(t * 0.6);
  // the light goes as the Sun sinks
  v *= 1.0 - 0.38 * dusk;

  return vec4f(palette(v, dusk), v);
}
